import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from '../services/authentication.service';
import { StorageService } from '../utils/storage.service';

// APIs propias: solo a ellas se les envía la sesión y el idioma. Un servicio externo (Google, S3, ipify…)
// no debe recibir el token, y la cabecera Authorization además obliga a una consulta CORS previa que muchos
// rechazan.
const OWN_API_URLS: string[] = [
	environment.apiUrl,
	environment.certero?.apiUrl,
	environment.certero?.notificationPushUrl,
	environment.ia?.apiUrl,
	environment.apiAwsLambdaUrl
].filter((url): url is string => !!url);

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
	private _auth = inject(AuthenticationService);
	private _translateService = inject(TranslateService);

	private readonly _storageService = inject(StorageService);

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (!this._isOwnApi(request.url)) return next.handle(request);

		const headers: Record<string, string> = {};

		// Agregar siempre el idioma si está disponible
		const currentLang = this._translateService.currentLang;
		if (currentLang) headers['accept-language'] = currentLang;

		// En una recarga del navegador, AuthenticationService todavía no ha sido
		// rehidratado cuando pueden salir las primeras peticiones. localStorage es la
		// fuente persistente de la sesión, así que se usa como fallback hasta que el
		// AuthGuard termine de reconstruir el estado en memoria.
		const storedSession = this._storageService.getAuthSession();
		let token = this._auth.currentUser?.access_token || storedSession?.access_token;
		if (request.url.includes('certero')) {
			token = this._auth.currentUser?.certero?.access_token || storedSession?.certero?.access_token;
		}

		if (token) headers['Authorization'] = `Bearer ${token}`;
		const clonedRequest = request.clone({ setHeaders: headers });

		return next.handle(clonedRequest);
	}

	// Las rutas relativas (assets, proxy de desarrollo) son del propio sitio
	private _isOwnApi(url: string): boolean {
		if (!/^https?:\/\//i.test(url)) return true;
		return OWN_API_URLS.some((base) => url.startsWith(base));
	}
}
